import React, { useState } from 'react';
import { Send, User, Mail, Building2, Phone, MessageSquare, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const DEMO_EMAIL = 'selim@pdtech.com.tr';

const DemoForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Ad Soyad zorunludur';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-posta zorunludur';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi girin';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefon zorunludur';
    } else if (!/^[+]?[0-9\s-]{10,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Geçerli bir telefon numarası girin';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    // Prepare email content
    const emailSubject = `CAPSNA Demo Talebi - ${formData.company || formData.name}`;
    const emailBody = `
Yeni Demo Talebi
================

Ad Soyad: ${formData.name}
E-posta: ${formData.email}
Telefon: ${formData.phone}
Şirket: ${formData.company || 'Belirtilmedi'}

Mesaj:
${formData.message || 'Mesaj yok'}

---
Bu talep CAPSNA web sitesinden gönderilmiştir.
    `.trim();
    
    // Open email client with pre-filled content
    const mailtoLink = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Simulate a small delay for UX
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Open mailto link
    window.location.href = mailtoLink;
    
    setIsLoading(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <section id="demo" className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-4">
              E-posta İstemciniz Açıldı!
            </h2>
            <p className="text-lg text-slate-600 mb-4">
              Demo talebinizi <strong>{DEMO_EMAIL}</strong> adresine gönderebilirsiniz.
            </p>
            <p className="text-slate-500 mb-8">
              E-posta istemciniz açılmadıysa, lütfen yukarıdaki adrese manuel olarak e-posta gönderin.
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({ name: '', email: '', company: '', phone: '', message: '' });
              }}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              Yeni Talep Oluştur
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="demo" className="py-24 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold mb-4">
            İletişim
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Demo
            <span className="text-blue-600"> Talep Edin</span>
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-600">
            CAPSNA'nın kurumunuza nasıl değer katabileceğini görmek için
            ücretsiz demo talebinde bulunun.
          </p>
        </div>

        {/* Form Container */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 font-medium flex items-center gap-2">
                    <User size={16} className="text-slate-400" />
                    Ad Soyad <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Adınızı girin"
                    className={`h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 ${errors.name ? 'border-red-500' : ''}`}
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium flex items-center gap-2">
                    <Mail size={16} className="text-slate-400" />
                    E-posta <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="ornek@sirket.com"
                    className={`h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.email}
                    </p>
                  )}
                </div>

                {/* Company */}
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-slate-700 font-medium flex items-center gap-2">
                    <Building2 size={16} className="text-slate-400" />
                    Şirket
                  </Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Şirket adınız"
                    className="h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 font-medium flex items-center gap-2">
                    <Phone size={16} className="text-slate-400" />
                    Telefon <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+90 5XX XXX XX XX"
                    className={`h-12 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 ${errors.phone ? 'border-red-500' : ''}`}
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-sm flex items-center gap-1">
                      <AlertCircle size={14} /> {errors.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-slate-700 font-medium flex items-center gap-2">
                  <MessageSquare size={16} className="text-slate-400" />
                  Mesajınız
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="İhtiyaçlarınız ve beklentileriniz hakkında kısaca bilgi verin..."
                  className="min-h-32 rounded-xl border-slate-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-14 rounded-xl text-lg font-semibold shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 transition-all duration-300"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Hazırlanıyor...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Demo Talep Et
                    <Send size={20} />
                  </span>
                )}
              </Button>

              {/* Info Note */}
              <p className="text-center text-sm text-slate-500">
                <span className="text-red-500">*</span> ile işaretli alanlar zorunludur.
                Talebiniz <strong>{DEMO_EMAIL}</strong> adresine iletilecektir.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoForm;
